import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Inject,
  input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Params, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {Breadcrumb} from 'src/app/types/Breadcrumb';
import {DefaultPage} from '../../../../../../app-common/const';
import {DefaultValuesService} from '../../../../../../services/default-values.service';
import {PageViewsGa4ResolverService} from '../../../../../../services/page-views-ga4-resolver.service';
import {ToolsService} from '../../../../../../services/tools.service';
import {UrlBuilderService} from '../../../../../../services/url-builder.service';
import {CareproviderUnitType} from '../../../../../../types/CareproviderUnitType';
import {DataLayerParams} from '../../../../../../types/DataLayerParams';
import {DataLayerParamsPageViewGA4} from '../../../../../../types/DataLayerParamsPageViewGA4';
import {DLPageTypeEnum} from '../../../../../../enums/DataLayerEnums';
import {RoutesAddonsEnums, RoutesEnums} from '../../../../../../enums/RoutesEnums';
import {SearchResultsStateEnum} from '../../../../../../enums/SearchEnums';
import {MetaTypesEnum, UrlTermsSymbolsEnum} from '../../../../../../enums/SeoEnums';
import {MetaTag} from '../../../../../../types/MetaTag';
import {QueryParams} from '../../../../../../types/QueryParams';
import {RouterData} from '../../../../../../types/RouterData';
import {SearchResultCareProviders} from '../../../../../../types/SearchResultCareProviders';
import {SearchResultsDescription} from '../../../../../../types/SearchResultsDescription';
import {SearchState} from '../../../../../../types/SearchState';
import {LocalStateService} from '../../../../services/local-state.service';
import {SearchBreadcrumbsService} from '../../../../services/search-breadcrumbs.service';
import {SearchConnectionsService} from '../../../../services/search-connections.service';
import {SearchMetaTagsService} from '../../../../services/search-meta-tags.service';
import {SearchRedirectsService} from '../../../../services/search-redirects.service';
import {SearchCategoryInfoAggregations} from 'src/app/types/SearchCategoryInfoAggregations';
import {WindowSizeService} from '../../../../../../services/window-size.service';
import {PluralService} from '../../../../../../services/plural.service';
import {SearchResultContent} from '../../../../../../types/SearchResultContent';
import {SearchResultsCareproviderUnitItem} from '../../../../../../types/SearchResultsCareproviderUnitItem';
import {SearchService} from '../../../../../../services/search/search.service';
import {GtmService} from '../../../../../../services/gtm.service';
import {DataLayerListItemsTag} from '../../../../../../types/DataLayerListItemsTag';
import {GtmEventEnum} from '../../../../../../enums/GoogleEnums';
import {DataLayerListItemsEcommerceItem} from '../../../../../../types/DataLayerListItemsEcommerceItem';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {SeoMetaService} from '../../../../../../services/seo-meta.service';
import {ElementPositionEnum} from '../../../../../../enums/CssClassEnums';
import {CpruTabsService} from '../../../../../../services/cpru-tabs.service';
import {CpruTab} from '../../../../../../types/CpruTab';
import {LayoutService} from '../../../../../layout/layout.service';
import {ProsEndConsNumerositySeEnum} from '../../../../../../enums/ProsAndConsEnum';
import {StaticSidebarPuffsService} from '../../../../../../services/static-sidebar-puffs.service';

interface UnitTypesCount {
  unitType: CareproviderUnitType;
  count: number;
}

@Component({
  selector: 'app-search-v6',
  templateUrl: './search-v6.component.html',
  styleUrls: ['./search-v6.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchV6Component implements OnInit, OnDestroy {
  public cpruTabsService = inject(CpruTabsService);
  public tabs = this.cpruTabsService.tabs;
  public highlightedSection = this.cpruTabsService.highlightedSection;

  public isConditionalRedirected = input<boolean>(false);
  public total = signal<number>(null);
  public totalOtherTab = signal<number>(null);
  public pageSize = signal<number>(null);
  public page = signal<number>(null);
  public results = signal<SearchResultsCareproviderUnitItem[]>(null);
  public searchDescription = signal<SearchResultsDescription>(null);
  public searchResultContent = signal<SearchResultContent>(null);
  public searchCategoryInfoAggregations = signal<SearchCategoryInfoAggregations>(null);
  public routerQueryParams = signal<QueryParams>(null);
  public baseUrl: string[] = ['/', RoutesEnums.SEARCH];
  public breadcrumbs = signal<Breadcrumb[]>(null);
  public searchResultsState = signal<SearchResultsStateEnum>(SearchResultsStateEnum.LOADING);
  public isRedirected = signal<boolean>(false);
  public routerData = signal<RouterData>(null);
  public cpruTypes = signal<number[]>([]);
  public pageTitle = signal<string>('');
  public customParams = signal<DataLayerParams>(null);
  public areCpruTypesLoaded = signal<boolean>(false);
  public pageType: DLPageTypeEnum = DLPageTypeEnum.SEARCH_PAGE;
  public relatedLink = computed<string[]>(() => {
    const link: string[] = [];
    link.push('/');
    link.push(RoutesEnums.RELATED);
    if (this.searchState()?.caregiverUnitTypeUrlPath !== null && this.searchState()?.caregiverUnitTypeUrlPath !== '') {
      link.push(RoutesAddonsEnums.RELATED_CAREPROVIDER);
      link.push(`${this.searchState()?.caregiverUnitTypeUrlPath}-${UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE}`);
    }
    if (this.searchState()?.careServiceTypeUrlPath !== null && this.searchState()?.careServiceTypeUrlPath !== '') {
      link.push(RoutesAddonsEnums.RELATED_CARESERVICE);
      link.push(`${this.searchState()?.careServiceTypeUrlPath}-${UrlTermsSymbolsEnum.SERVICE_TYPE}`);
    }
    if (this.searchState()?.symptomUrlPath !== null && this.searchState()?.symptomUrlPath !== '') {
      link.push(RoutesAddonsEnums.RELATED_SYMPTOM);
      link.push(`${this.searchState()?.symptomUrlPath}-${UrlTermsSymbolsEnum.SYMPTOM}`);
    }
    return link;
  });
  protected placeholderStyle = signal<{[klass: string]: any} | null | undefined>({display: 'none'});
  protected topSectionCssClass = signal<string>(ElementPositionEnum.NORMAL);
  protected readonly RoutesAddonsEnums = RoutesAddonsEnums;
  private subscriptions: Subscription[];
  private readonly mobileTreshold: number = 768;
  private windowSizeService = inject(WindowSizeService);
  private size = this.windowSizeService.windowSize;
  public isMobile = computed<boolean>(() => this.size() && this.size()[0] < this.mobileTreshold);
  private searchService = inject(SearchService);
  public searchState = this.searchService.searchState;
  private metaTags = this.searchService.metaTags;
  private cmsCategories = this.searchService.cmsCategories;
  private adCampaignOwner = this.searchService.adCampaignOwner;
  private searchResults = this.searchService.searchResults;
  private searchResultsProcessed = signal<SearchResultCareProviders>(null);
  private isRedirectedProcessed = signal<boolean>(null);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private urlBuilderService: UrlBuilderService,
    private tools: ToolsService,
    private defaults: DefaultValuesService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private searchMetaTags: SearchMetaTagsService,
    private localState: LocalStateService,
    private searchRedirects: SearchRedirectsService,
    private breadcrumbsService: SearchBreadcrumbsService,
    private searchConnections: SearchConnectionsService,
    private pluralService: PluralService,
    private gtmService: GtmService,
    private sidebarPuffsService: StaticSidebarPuffsService,
    private metaService: SeoMetaService,
    private layoutService: LayoutService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.resetInitialValues();

    effect(
      () => {
        this.processCmsCategories(this.cmsCategories());
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(
      () => {
        this.processSearchState(this.searchState());
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(
      () => {
        if (
          !this.tools.isEqual(this.searchResults(), this.searchResultsProcessed()) ||
          this.isRedirected() !== this.isRedirectedProcessed()
        ) {
          this.searchResultsProcessed.set(this.searchResults());
          this.isRedirectedProcessed.set(this.isRedirected());
          this.processSearchResults(this.searchResults(), this.isRedirected());
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(
      () => {
        if (this.total() !== null) {
          this.updateTabs();
        }
      },
      {allowSignalWrites: true},
    );
    effect(
      () => {
        if (this.searchState() && this.tabs().length > 0) {
          this.cpruTabsService.setHighlightedSection(
            this.searchState().tab === RoutesAddonsEnums.SERP_SERVICES
              ? RoutesAddonsEnums.SERP_SERVICES
              : RoutesAddonsEnums.SERP_CAREPROVIDERS,
          );
        }
      },
      {allowSignalWrites: true},
    );
    this.layoutService.setShouldUseGreyBackground(true);
  }

  public ngOnInit() {
    this.getSearchResultRoute = this.getSearchResultRoute.bind(this);
    this.getSearchResultsQueryParams = this.getSearchResultsQueryParams.bind(this);
    this.onRouteChanged(true);
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public handleTabsInViewport({target, visible}: {target: Element; visible: boolean}): void {
    if (visible) {
      this.topSectionCssClass.set(ElementPositionEnum.NORMAL);
      this.placeholderStyle.set({display: 'none'});
    } else {
      const componentElement: HTMLElement = this.document.getElementById('top-section-container') as HTMLElement;
      const height = componentElement.offsetHeight;
      this.topSectionCssClass.set(ElementPositionEnum.FIXED_WITH_SEARCH);
      this.placeholderStyle.set({display: 'block', height: `${height}px`});
    }
  }

  public scrollToSearchDescription(event: boolean): void {
    const scrollToDescriptionCorrectionMobile: number = -160;
    const scrollToDescriptionCorrectionDesktop: number = -140;
    this.tools.scrollTo(
      'full-search-description',
      0,
      this.isMobile ? scrollToDescriptionCorrectionMobile : scrollToDescriptionCorrectionDesktop,
    );
  }

  public getSearchResultRoute(page: number): string[] {
    return page !== DefaultPage && page > 1 ? this.routerData().vardRouteLink : this.routerData().baseRouteLink;
  }

  public getSearchResultsQueryParams(page: number, pageParameterName: string): QueryParams {
    let newRouterQueryParams: QueryParams = {};

    if (page !== DefaultPage && page > 1) {
      newRouterQueryParams = this.tools.clone(this.routerData().vardRouteQueryParams);
      newRouterQueryParams[pageParameterName] = page;
    } else {
      newRouterQueryParams = this.tools.clone(this.routerData().baseRouteQueryParams);
      delete newRouterQueryParams[pageParameterName];
    }

    return newRouterQueryParams;
  }

  public onTabChange(index: number): void {
    setTimeout(() => {
      this.scrollToActiveTab();
    }, 0);
  }

  private removeAdjunctFromPositiveResponse(text: string): string {
    const pattern = new RegExp(
      `\\((${ProsEndConsNumerositySeEnum.MANY}|${ProsEndConsNumerositySeEnum.SOME}|${ProsEndConsNumerositySeEnum.SINGLE})\\)`,
      'g',
    );
    return text.replace(pattern, '').trim();
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRouteChanged(false);
        }
      }),
    );
  }

  private onRouteChanged(isInitialLoad: boolean): void {
    if (this.isConditionalRedirected() === false) {
      this.updateIsRedirected(this.route.snapshot.queryParams);
    }
  }

  private resetInitialValues(): void {
    this.total.update((total) => (this.tools.isNil(total) ? 0 : total));
    this.totalOtherTab.update((total) => (this.tools.isNil(total) ? 0 : total));
    this.pageSize.update((pageSize) => (this.tools.isNil(pageSize) ? 0 : pageSize));
    this.page.update((page) => (this.tools.isNil(page) ? 0 : page));
    this.results.update((results) => (this.tools.isNil(results) ? [] : results));
    this.searchDescription.update((searchDescription) =>
      !this.tools.isNil(searchDescription) ? searchDescription : this.defaults.getSearchResultDescription(),
    );
    this.searchResultContent.update((searchResultContent) =>
      !this.tools.isNil(searchResultContent) ? searchResultContent : this.defaults.getSearchResultContent(),
    );
    this.breadcrumbs.update((breadcrumbs) => (this.tools.isNil(breadcrumbs) ? [] : breadcrumbs));
    this.subscriptions = this.tools.isNil(this.subscriptions) ? [] : this.subscriptions;
  }

  private scrollToTheTop(): void {
    this.tools.scrollTo();
  }

  private processSearchState(state: SearchState): void {
    if (!this.tools.isNil(state)) {
      this.routerQueryParams.set(this.urlBuilderService.CreateSearchResultsQueryParams(state));
      this.routerData.set(this.urlBuilderService.CreateRouteData(state, this.baseUrl));
    }
  }

  private processSearchResults(searchResults: SearchResultCareProviders, isRedirected: boolean): void {
    if (
      !this.tools.isNil(searchResults) &&
      !this.tools.isNil(searchResults.searchResultContent) &&
      this.searchRedirects.CompareStateWithSearchResponseAndRedirect(searchResults.searchResultContent) === false
    ) {
      this.total.set(searchResults.total);
      this.totalOtherTab.set(searchResults.countItems);
      this.pageSize.set(searchResults.pageSize);
      this.page.set(searchResults.page);
      this.results.set(this.cleanupSearchResults(searchResults.hits));
      this.searchDescription.set(searchResults.searchDescription);
      this.searchResultContent.set(searchResults.searchResultContent);
      this.searchCategoryInfoAggregations.set(searchResults.searchCategoryInfoAggregations);
      this.searchConnections.updateAdsState(searchResults);

      if (this.total() <= 0 && this.searchResultContent().allSolved === false) {
        this.searchResultsState.set(SearchResultsStateEnum.NO_RESULTS);
        this.searchRedirects.RemoveEdgeConditionsFromSearchAndRedirect(
          searchResults.searchResultContent,
          searchResults.searchQueryDto,
        );
        return;
      } else {
        this.searchResultsState.set(SearchResultsStateEnum.LOADED);

        this.breadcrumbs.set(
          searchResults.searchResultContent.breadcrumbs
            ? this.breadcrumbsService.getBreadcrumbs(searchResults.searchResultContent.breadcrumbs)
            : [],
        );
        this.metaService.updateMetaTags(this.metaTags());
        this.searchMetaTags.updateLinkMetaTags(
          searchResults.page,
          searchResults.totalPages,
          this.localState.searchStateLocal(),
        );
        this.searchMetaTags.updateNoIndexTag(searchResults.noIndex);
        this.updatePageTitleFromSearchMetaTags();

        if (
          isRedirected === false &&
          this.searchResultContent().allSolved === false &&
          searchResults.timeslotsUpdated !== true
        ) {
          if (isPlatformBrowser(this.platformId)) {
            this.subscriptions.push(this.searchService.setupTimeslotsUpdate().subscribe());
          }
        }
        if (
          isRedirected === false &&
          this.searchResultContent().allSolved === false &&
          searchResults.timeslotsUpdated === true
        ) {
          this.customParams.set(
            this.createDataLayerCustomParams(searchResults.hits, searchResults.searchResultContent),
          );
          const customGa4Params: DataLayerParamsPageViewGA4 = {
            pageTitle: this.pageTitle(),
            pageType: DLPageTypeEnum.SEARCH_PAGE,
            inferredCareProviderTypeAreaId: this.customParams().InferredCareproviderTypesArea,
            inferredCategoryTypeId: this.customParams().InferredCareproviderMainTypeID,
            mainCareProviderTypeId: this.searchState().caregiverUnitTypeUrlPath
              ? this.customParams().InferredCareproviderMainTypeID
              : null,
            careServiceTypeId:
              searchResults.searchResultContent && searchResults.searchResultContent.careServiceType
                ? searchResults.searchResultContent.careServiceType.id
                : null,
            symptomId:
              searchResults.searchResultContent && searchResults.searchResultContent.symptom
                ? searchResults.searchResultContent.symptom.id
                : null,
            adCampaignOwner: this.adCampaignOwner(),
          };
          let timeslots: number = 0;
          searchResults.hits.forEach((item) => {
            if (item.careServices && item.careServices.length > 0) {
              item.careServices.forEach((serviceItem) => {
                timeslots += serviceItem.timeSlots == null ? 0 : serviceItem.timeSlots.length;
              });
            }
          });
          customGa4Params.timeSlots = timeslots;
          this.pageViewGa4Resolver.trackPageView(customGa4Params);
          this.pushViewItemListEventToDataLayer();
        }
      }
      this.onDataLoadingFinish();
    }
  }

  private scrollToActiveTab(): void {
    const activeTab: HTMLElement | null = this.document.querySelector('.mat-tab-label-active');
    if (activeTab) {
      activeTab.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'});
    }
  }

  private cleanupSearchResults(results: SearchResultsCareproviderUnitItem[]): SearchResultsCareproviderUnitItem[] {
    return results?.map((result) => {
      return {
        ...result,
        topPositiveResponse: result.topPositiveResponse
          ? this.removeAdjunctFromPositiveResponse(result.topPositiveResponse)
          : '',
        suitableFor: result.suitableFor ? this.capitalizeFirstLetter(result.suitableFor) : '',
      };
    });
  }

  private capitalizeFirstLetter(text: string): string {
    if (!text) {
      return '';
    }

    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private pushViewItemListEventToDataLayer(): void {
    const items: DataLayerListItemsEcommerceItem[] = [];
    this.results().forEach((item) => {
      const ecommerceItem: DataLayerListItemsEcommerceItem = {
        item_id: item.itemId,
        cpru_id: item.id,
        careservice_id: item.careServiceId,
        Pagetype: DLPageTypeEnum.SEARCH_PAGE,
        careservice_type: item.careServiceTypeId,
        mainCareProviderTypeId: item.mainCaregiverUnitType ? item.mainCaregiverUnitType.id : null,
      };
      items.push(ecommerceItem);
    });

    if (items.length > 0) {
      const tag: DataLayerListItemsTag = {
        event: GtmEventEnum.VIEW_ITEM_LIST,
        ecommerce: {
          items: items,
        },
      };
      this.gtmService.pushTag({ecommerce: null}, true);
      this.gtmService.pushTag(tag, true);
    }
  }

  private updatePageTitleFromSearchMetaTags(): void {
    if (!this.tools.isNil(this.metaTags())) {
      const tag: MetaTag = this.metaTags().find((item) => item.type === MetaTypesEnum.TITLE);
      if (!this.tools.isNil(tag)) {
        this.pageTitle.set(tag.value);
      }
    } else {
      this.pageTitle.set('');
    }
  }

  private createDataLayerCustomParams(
    items: SearchResultsCareproviderUnitItem[],
    searchResultContent: SearchResultContent,
  ): DataLayerParams {
    const customParams: DataLayerParams = {
      PageType: DLPageTypeEnum.SEARCH_PAGE,
    };

    const itemsToCheck: SearchResultsCareproviderUnitItem[] = items.slice(0, 5);
    let unitTypes: CareproviderUnitType[] = [];
    itemsToCheck.forEach((item) => {
      unitTypes = [...unitTypes, ...item.caregiverUnitTypes, ...[item.mainCaregiverUnitType]];
    });
    const groupedUnitTypes = this.tools.groupBy(unitTypes, 'id');
    const countedUnitTypes: UnitTypesCount[] = [];
    Object.keys(groupedUnitTypes).forEach((key) => {
      countedUnitTypes.push({
        unitType: groupedUnitTypes[key][0],
        count: groupedUnitTypes[key].length,
      });
    });
    countedUnitTypes.sort(this.tools.sortBy('count'));
    const mostCommonUnitType: CareproviderUnitType =
      countedUnitTypes && countedUnitTypes.length > 0 ? countedUnitTypes.pop().unitType : null;
    if (!this.tools.isNil(mostCommonUnitType)) {
      customParams.InferredCareproviderMainTypeID = mostCommonUnitType.id;

      if (!this.tools.isNil(mostCommonUnitType.area)) {
        customParams.InferredCareproviderTypesArea = mostCommonUnitType.area.id;
      }

      if (!this.tools.isNil(mostCommonUnitType.segment)) {
        customParams.InferredCareproviderTypesSegment = mostCommonUnitType.segment.id;
      }
    }

    if (!this.tools.isNil(searchResultContent) && !this.tools.isNil(searchResultContent.caregiverUnitType)) {
      customParams.InferredCareproviderMainTypeID = searchResultContent.caregiverUnitType.id;

      if (!this.tools.isNil(searchResultContent.caregiverUnitType.area)) {
        customParams.InferredCareproviderTypesArea = searchResultContent.caregiverUnitType.area.id;
      }

      if (!this.tools.isNil(searchResultContent.caregiverUnitType.segment)) {
        customParams.InferredCareproviderTypesSegment = searchResultContent.caregiverUnitType.segment.id;
      }
    }

    return customParams;
  }

  private updateIsRedirected(queryParams: Params): void {
    this.isRedirected.set(
      queryParams[UrlTermsSymbolsEnum.AFTER_REDIRECT] != null &&
        queryParams[UrlTermsSymbolsEnum.AFTER_REDIRECT] === '1',
    );
  }

  private onDataLoadingFinish(): void {
    this.scrollToTheTop();
  }

  private processCmsCategories(result: number[]): void {
    this.cpruTypes.set(result);
    if (result !== null) {
      this.areCpruTypesLoaded.set(true);
      this.sidebarPuffsService.reloadCombinedPuffs(RoutesEnums.SEARCH, null, this.cpruTypes(), null, null, null);
    } else {
      this.areCpruTypesLoaded.set(false);
    }
  }

  private updateTabs(): void {
    const tabs: CpruTab[] = [];

    tabs.push({
      name: RoutesAddonsEnums.SERP_SERVICES,
      label: 'Behandlingar',
      mobileOnly: false,
      desktopOnly: false,
      icon: 'trust',
      count: this.searchState().tab === RoutesAddonsEnums.SERP_SERVICES ? this.total() : this.totalOtherTab(),
    });

    tabs.push({
      name: RoutesAddonsEnums.SERP_CAREPROVIDERS,
      label: 'Mottagningar',
      mobileOnly: false,
      desktopOnly: false,
      icon: 'hospital',
      count: this.searchState().tab === RoutesAddonsEnums.SERP_CAREPROVIDERS ? this.total() : this.totalOtherTab(),
    });

    this.cpruTabsService.updateTabs(tabs);
  }
}
